import { injectable } from 'inversify';
import { SeoMetaDataPrerenderRefreshCompletedEvent } from '@/platform/events/SeoMetaDataPrerenderRefreshCompletedEvent';
import { CanonicalUrl } from '@/seo/CanonicalUrl';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { ref } from 'vue';
import { ConvergingTask } from '@/platform/ConvergingTask';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { IndexableUrlRefreshCompletedEvent } from '@/platform/events/IndexableUrlRefreshCompletedEvent';
import { ISeoMetaDataPrerenderInfo } from './ISeoMetaDataPrerenderInfo';
import { PrerenderStatusCode } from './PrerenderStatusCode';

@injectable()
export class SeoMetaDataPrerender extends ConvergingTask<ISeoMetaDataPrerenderInfo, null> implements ISubscriber<IndexableUrlRefreshCompletedEvent> {
	public readonly prerenderStatusCode = ref<number>(200);
	public readonly showPrerenderHeader = ref<boolean>(false);
	public readonly prerenderHeader = ref<string>('');

	private readonly eventAggregationService: EventAggregationService;
	private readonly canonicalUrl: CanonicalUrl;

	constructor(eventAggregationService: EventAggregationService, canonicalUrl: CanonicalUrl) {
		super();

		this.eventAggregationService = eventAggregationService;
		this.canonicalUrl = canonicalUrl;

		eventAggregationService.subscribeTo(IndexableUrlRefreshCompletedEvent.EventType, this);
	}

	public async notify(event: IndexableUrlRefreshCompletedEvent): Promise<void> {
		if (IndexableUrlRefreshCompletedEvent.is(event)) {
			await this.refresh();
		}
	}

	private async refresh(): Promise<void> {
		await this.execute(null);
	}

	protected override async run(): Promise<ISeoMetaDataPrerenderInfo> {
		if (!this.canonicalUrl.isSelfCanonical()) {
			return {
				prerenderStatusCode: PrerenderStatusCode.REDIRECT,
				showPrerenderHeader: true,
				prerenderHeader: `Location: ${this.canonicalUrl.canonical.value}`
			};
		}

		return {
			prerenderStatusCode: PrerenderStatusCode.OK,
			showPrerenderHeader: false,
			prerenderHeader: ''
		};
	}

	protected override commit(result: ISeoMetaDataPrerenderInfo): void {
		this.prerenderStatusCode.value = Number(result.prerenderStatusCode);
		this.showPrerenderHeader.value = result.showPrerenderHeader;
		this.prerenderHeader.value = result.prerenderHeader;
	}

	protected override afterEachRun(): void {
		this.eventAggregationService.publishTo(
			SeoMetaDataPrerenderRefreshCompletedEvent.EventType,
			new SeoMetaDataPrerenderRefreshCompletedEvent({
				prerenderStatusCode: this.prerenderStatusCode.value,
				showPrerenderHeader: this.showPrerenderHeader.value,
				prerenderHeader: this.prerenderHeader.value
			})
		);
	}

	protected override queued(): boolean {
		return false;
	}
}
